import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import dayjs, { type Dayjs } from 'dayjs';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import TextButton from '@/components/@IntranetLibrary/Button/TextButton';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import ExternalLoanBody from '@/components/Approval/@components/ExternalLoan';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import { getExternalLoanDraftSourceUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildExternalLoanSubject,
  EXTERNAL_LOAN_LOAD_FAILED_MESSAGE,
  EXTERNAL_LOAN_NOT_SEARCHED_MESSAGE,
  externalLoanDataFromDraftSource,
  type ExternalLoanDraftSource,
} from '@/utils/approval/externalLoan';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type ExternalLoanFieldProps = {
  /** 신규는 양식 응답, 편집은 저장값 복원이 끝난 뒤 true */
  ready: boolean;
};

const DATE_FORMAT = 'YYYY-MM-DD';

/** SAP 거절(424)은 서버 문구 「…: SAP Exception: {SAP 문구}」에서 SAP 문구만 */
const sapFailedMessage = (error: unknown): string | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 424) return null;
  const message = error.response.data?.message;
  if (typeof message !== 'string') return null;
  return message.split('SAP Exception:').pop()?.trim() || null;
};

/**
 * 외담대 발행(217) — 구매카드 발급일을 고르고 「조회」를 눌러야 SAP 발행내역을 불러옴(날짜만 바꾸면 화면 그대로).
 * 조회할 때 제목도 그 발급일로 다시 채움. SAP 진입(`issueDate`)은 그 날짜로 한 번 자동 조회.
 * 편집은 저장 내역을 그대로 보여 주고, 다시 조회하면 새 결과로 바꿈
 */
const ExternalLoanField = ({ ready }: ExternalLoanFieldProps) => {
  const { t, i18n } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'externalLoan' });
  const requestIdRef = useRef(0);
  const startedRef = useRef(false);

  useEffect(
    () => () => {
      requestIdRef.current += 1;
    },
    [],
  );

  const search = useCallback(
    async (issueDate: string) => {
      const requestId = ++requestIdRef.current;
      const current = getValues('externalLoan');
      if (!current) return;
      setValue(
        'externalLoan',
        { ...current, issueDate, loading: true, error: null },
        { shouldDirty: true },
      );
      setValue('subject', buildExternalLoanSubject(issueDate, t), {
        shouldDirty: true,
      });
      const finish = (patch: Partial<NonNullable<typeof current>>) => {
        const latest = getValues('externalLoan');
        if (latest) {
          setValue('externalLoan', { ...latest, loading: false, ...patch });
        }
      };
      try {
        const res = await getNestedDataFetcher<ExternalLoanDraftSource>(
          getExternalLoanDraftSourceUrl(issueDate),
        );
        if (requestId !== requestIdRef.current) return;
        finish({ data: externalLoanDataFromDraftSource(res, issueDate) });
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        finish({
          data: null,
          error: sapFailedMessage(error) ?? EXTERNAL_LOAN_LOAD_FAILED_MESSAGE,
        });
      }
    },
    [getValues, setValue, t],
  );

  useEffect(() => {
    if (!ready || startedRef.current) return;
    startedRef.current = true;
    const current = getValues('externalLoan');
    if (current?.issueDate && !current.data) search(current.issueDate);
  }, [ready, getValues, search]);

  const handleDateChange = (value: Dayjs | null) => {
    const current = getValues('externalLoan');
    if (!current) return;
    setValue('externalLoan', {
      ...current,
      issueDate: value?.isValid() ? value.format(DATE_FORMAT) : null,
    });
  };

  if (!state) return null;

  const renderResult = () => {
    if (state.loading) {
      return (
        <ApprovalNoticeBox role="status">
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      );
    }
    if (state.error) {
      const retryDate = state.issueDate;
      return (
        <NoticeWithAction role="alert">
          <span>
            {state.error === EXTERNAL_LOAN_LOAD_FAILED_MESSAGE
              ? t(EXTERNAL_LOAN_LOAD_FAILED_MESSAGE)
              : state.error}
          </span>
          {retryDate && (
            <TextButton
              type="button"
              variant="keyColor"
              size="medium"
              onClick={() => search(retryDate)}
            >
              {t('다시 불러오기')}
            </TextButton>
          )}
        </NoticeWithAction>
      );
    }
    if (!state.data) {
      return (
        <ApprovalNoticeBox>
          {t(EXTERNAL_LOAN_NOT_SEARCHED_MESSAGE)}
        </ApprovalNoticeBox>
      );
    }
    if (!state.data.items.length) {
      return (
        <ApprovalNoticeBox>
          {t('발급일 {{date}} 기준 발행 대상이 없습니다.', {
            date: state.data.issueDate,
          })}
        </ApprovalNoticeBox>
      );
    }
    return (
      <ExternalLoanBody
        key={`${state.data.issueDate}-${state.data.groupwareDocumentNumber}`}
        issueDate={state.data.issueDate}
        items={state.data.items}
        t={t}
        lang={i18n.language}
      />
    );
  };

  return (
    <FieldBlock className="external-loan-field">
      <SearchArea>
        <InfoSectionTitle>{t('구매카드 발급일')}</InfoSectionTitle>
        <Description>
          {t('선택한 발급일 기준으로 SAP 발행내역을 불러옵니다.')}
        </Description>
        <SearchRow>
          <DatePicker
            value={state.issueDate ? dayjs(state.issueDate) : null}
            onChange={handleDateChange}
            format={DATE_FORMAT}
            placeholder={DATE_FORMAT}
            sx={{ width: 180 }}
          />
          <BoxButton
            type="button"
            variant="keyColor"
            size="medium"
            disabled={!state.issueDate || state.loading}
            startIcon={
              state.loading ? <Spinner theme="white" size="16px" /> : undefined
            }
            onClick={() => state.issueDate && search(state.issueDate)}
          >
            {t('조회')}
          </BoxButton>
        </SearchRow>
      </SearchArea>
      {renderResult()}
    </FieldBlock>
  );
};

export default ExternalLoanField;

const SearchArea = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  paddingBottom: 24,
  marginBottom: 24,
  borderBottom: '1px solid var(--dk-line, #E7E9EC)',
});

const Description = styled('p')({
  margin: 0,
  fontSize: 12,
  lineHeight: '16px',
  color: 'var(--dk-text-secondary, #6B7280)',
});

const SearchRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 4,
});

const NoticeWithAction = styled(ApprovalNoticeBox)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
});
